/**
 * Player response signals.
 *
 * Three preset chips on every note / playlist response: `Got it`,
 * `Need help`, `I tried this`, optionally followed by a short
 * free-text note.
 *
 * Backend strategy: the existing `feedback_reviews.reflection` text
 * column already carries arbitrary free-text via the
 * `POST /api/my-feedback/review` endpoint. We encode the structured
 * signal as a `[SIGNAL:GOT_IT]` / `[SIGNAL:NEED_HELP]` /
 * `[SIGNAL:TRIED_THIS]` prefix at the head of the reflection field,
 * optionally followed by " — <note>". The bundle round-trips the
 * raw string; the decoder here splits it back out for the UI.
 *
 * This stays a frontend-only contract for PR 6 — the plan explicitly
 * defers a dedicated `signal` column to "later when coach-side
 * visibility lands" and recommends "first pass uses existing
 * review/reflection APIs only". A future backend migration can
 * promote the encoded prefix into a structured column without
 * breaking the player-facing surface.
 */

export type PlayerResponseSignal = "got_it" | "need_help" | "tried_this";

export const PLAYER_RESPONSE_SIGNALS: ReadonlyArray<PlayerResponseSignal> = [
  "got_it",
  "need_help",
  "tried_this",
];

export type PlayerResponseSignalConfig = {
  signal: PlayerResponseSignal;
  /** Short button label — "Got it" / "Need help" / "I tried this". */
  label: string;
  /** Emoji glyph (mockup uses ✓ / ? / ⚡); paired with the label. */
  glyph: string;
  /** Coach-facing tone label (used on the coach roster signal cell). */
  toneLabel: string;
};

export const PLAYER_RESPONSE_SIGNAL_CONFIG: Record<
  PlayerResponseSignal,
  PlayerResponseSignalConfig
> = {
  got_it: { signal: "got_it", label: "Got it", glyph: "✓", toneLabel: "Acknowledged" },
  need_help: { signal: "need_help", label: "Need help", glyph: "?", toneLabel: "Needs follow-up" },
  tried_this: { signal: "tried_this", label: "I tried this", glyph: "⚡", toneLabel: "Player tried it" },
};

/** Sentinel prefix that the reflection text round-trips through.
 * Kept short + machine-readable so a coach who manually inspects
 * the reflection still sees it readably; a future backend column
 * migration would strip it on persist. */
const SIGNAL_PREFIX_RE = /^\[SIGNAL:(GOT_IT|NEED_HELP|TRIED_THIS)\](?:\s*[—-]\s*)?(.*)$/su;

const SIGNAL_TO_TOKEN: Record<PlayerResponseSignal, string> = {
  got_it: "GOT_IT",
  need_help: "NEED_HELP",
  tried_this: "TRIED_THIS",
};

const TOKEN_TO_SIGNAL: Record<string, PlayerResponseSignal> = {
  GOT_IT: "got_it",
  NEED_HELP: "need_help",
  TRIED_THIS: "tried_this",
};

/** Encode a {signal?, note?} pair into the reflection text payload.
 *
 *   - Just a signal: `[SIGNAL:GOT_IT]`
 *   - Signal + note: `[SIGNAL:TRIED_THIS] — I worked on this in Tuesday's training.`
 *   - No signal, free-text only: returns the trimmed note (legacy
 *     reflection path stays usable).
 *   - Empty input: returns "" so the caller can clear a reflection.
 */
export function encodeReflection(input: {
  signal?: PlayerResponseSignal | null;
  note?: string | null;
}): string {
  const note = (input.note ?? "").trim();
  if (!input.signal) return note;
  const token = SIGNAL_TO_TOKEN[input.signal];
  if (!note) return `[SIGNAL:${token}]`;
  return `[SIGNAL:${token}] — ${note}`;
}

/** Decode the reflection text payload back into {signal, note}.
 * Missing / empty / un-prefixed text decodes as `{signal: null,
 * note}`. */
export function decodeReflection(reflection: string | null | undefined): {
  signal: PlayerResponseSignal | null;
  note: string;
} {
  const raw = (reflection ?? "").trim();
  if (!raw) return { signal: null, note: "" };
  const match = SIGNAL_PREFIX_RE.exec(raw);
  if (!match) return { signal: null, note: raw };
  const token = match[1] ?? "";
  const note = (match[2] ?? "").trim();
  const signal = TOKEN_TO_SIGNAL[token] ?? null;
  return { signal, note };
}

/** Index the bundle's `reviews[]` so the cards can look up the
 * player's response for a given note / playlist / clip. Keys are
 * namespaced (`note:<id>` / `playlist:<id>` / `clip:<id>`) so id 5
 * doesn't collide across kinds. PR 7 added the `clip:` namespace
 * once the backend started accepting `clip_id` reviews. */
export type PlayerResponseIndex = {
  forNote: (noteId: number) => ReturnType<typeof decodeReflection>;
  forPlaylist: (playlistId: number) => ReturnType<typeof decodeReflection>;
  forClip: (clipId: number) => ReturnType<typeof decodeReflection>;
  has: (key: string) => boolean;
};

export function buildPlayerResponseIndex(
  reviews: ReadonlyArray<{
    note_id?: number | null;
    playlist_id?: number | null;
    clip_id?: number | null;
    reflection?: string;
  }>,
): PlayerResponseIndex {
  const map = new Map<string, ReturnType<typeof decodeReflection>>();
  for (const r of reviews) {
    const decoded = decodeReflection(r.reflection ?? "");
    if (typeof r.note_id === "number") {
      map.set(`note:${r.note_id}`, decoded);
    }
    if (typeof r.playlist_id === "number") {
      map.set(`playlist:${r.playlist_id}`, decoded);
    }
    if (typeof r.clip_id === "number") {
      map.set(`clip:${r.clip_id}`, decoded);
    }
  }
  return {
    forNote: (noteId: number) =>
      map.get(`note:${noteId}`) ?? { signal: null, note: "" },
    forPlaylist: (playlistId: number) =>
      map.get(`playlist:${playlistId}`) ?? { signal: null, note: "" },
    forClip: (clipId: number) =>
      map.get(`clip:${clipId}`) ?? { signal: null, note: "" },
    has: (key: string) => map.has(key),
  };
}
